/* A character's full printed set, laid out like the official PDFs: the class sheet,
   its guide, the heritage and domain card page, and the pages some classes add.
   `style` switches between the site's theme and the classic Daggerheart print look. */

import type { SheetPageName, SheetSection } from './content/sheetText';
import { Catalog, classFeatures, featureList } from './catalog';
import { BlankPart, blankOut, Character, deriveStats, Track } from './model';
import SheetPage from './SheetPage';
import GuidePage from './GuidePage';
import CardsPage, { TransformationPage } from './CardsPage';
import MotherboardPage from './MotherboardPage';
import { BeastformPages, CompanionPage, MulticlassPage, StanceSheetChoice, StancesPage } from './ExtraPages';
import type { PageProps } from './SheetParts';
import './CharacterDocument.css';

export type SheetStyle = 'site' | 'classic';

interface Props {
  character: Character;
  catalog: Catalog;
  style?: SheetStyle;
  /** Sheet areas to light up (the builder's current step). */
  highlight?: SheetSection[];
  /** Crops each page to its lit areas. */
  closeUp?: boolean;
  /** Only these pages, without the class extras (the builder preview). */
  pages?: SheetPageName[];
  onMark?: (track: Track, count: number) => void;
  /** Changes the character from a page (the Martial Stances page's stances and layout); shows that page's layout choice. */
  onSet?: (patch: Partial<Character>) => void;
  /** Shows gear buttons that open the builder step for an area. */
  onEdit?: (section: SheetSection) => void;
  /** Shows a Transform button on each Beastform the Druid can take. */
  onBeastform?: (form: string) => void;
  /** Makes a class choice written on a page (patron, element) a button that opens its dialog. */
  onChoose?: PageProps['onChoose'];
  /** 'actual' shows the pages at full size on narrow screens, scrolling sideways. */
  zoom?: 'fit' | 'actual';
  /** Parts a printed copy leaves empty, to fill in by hand. */
  blank?: readonly BlankPart[];
}

const NONE: SheetSection[] = [];

export default function CharacterDocument({ character, catalog, style = 'site', zoom, highlight = NONE, closeUp, pages, onMark, onSet, onEdit, onBeastform, onChoose, blank }: Props) {
  const c = blank ? blankOut(character, blank) : character;
  const props = { c, catalog, stats: deriveStats(c, catalog), highlight, closeUp, onMark, onSet, onEdit, onBeastform, onChoose, blank };
  const cls = catalog.cls(c.className);
  // A blank sheet has no subclass yet, so it carries every subclass's extra pages.
  const subclasses = Object.values(cls?.subclasses ?? {}).filter((s) => !c.subclass || s.name === c.subclass);
  const stances = subclasses.find((s) => s.martialForm);
  const show = (page: SheetPageName) => !pages || pages.includes(page);
  const extras = !pages;

  return (
    <div className="cs-doc" data-style={style} data-zoom={zoom}>
      {show('sheet') && <SheetPage {...props} />}
      {show('guide') && <GuidePage {...props} />}
      {show('cards') && <CardsPage {...props} />}
      {extras && classFeatures(cls).some((f) => f.name === 'Beastform') && <BeastformPages {...props} />}
      {extras && subclasses.some((s) => featureList(s.foundation).some((f) => f.name === 'Companion')) && <CompanionPage {...props} />}
      {extras && stances?.martialForm && onSet && <StanceSheetChoice c={c} onSet={onSet} />}
      {extras && stances?.martialForm && <StancesPage {...props} subclass={stances.name} form={stances.martialForm} />}
      {extras && c.multiclass && <MulticlassPage {...props} />}
      {extras && c.useIkonis && <MotherboardPage {...props} />}
      {extras && c.transformation && <TransformationPage {...props} />}
    </div>
  );
}
